import snakeCase from "lodash.snakecase";
import { de } from "./languages/de.locale";
import { en } from "./languages/en.locale";

export const locales: Locale[] = ["de", "en"];

export const localizations = {
	resources: {
		de,
		en,
	},
	lng: "de",
	fallbackLng: "en",
	interpolation: {
		escapeValue: false,
	},
};

export * from "./languages/de.locale";
export * from "./languages/en.locale";
export type * from "./types";
export type Locale = keyof (typeof localizations)["resources"];
type Leaves<T> = T extends object
	? {
			[K in keyof T]: `${Exclude<K, symbol>}${Leaves<T[K]> extends never ? "" : `.${Leaves<T[K]>}`}`;
		}[keyof T]
	: never;

type TranslationLeaves = Leaves<(typeof en)["translation"]>;
export type TranslationKey = TranslationLeaves;
/** The locale tables `localized` reads, by language. */
const TABLES: Record<Locale, unknown> = { de, en };

/**
 * The language a user who never chose one reads in. German, because the organisations using the
 * application work in German; a stored `users.language` of `null` resolves to it.
 */
export const DEFAULT_LOCALE: Locale = "de";

/**
 * Whether a value names one of the supported locales — the guard for a stored or posted language.
 *
 * @param value - Anything a column, a request or `localStorage` may hold.
 * @returns True when the value is exactly one of `locales`.
 */
export function isLocale(value: unknown): value is Locale {
	return typeof value === "string" && (locales as string[]).includes(value);
}

/**
 * The locale a stored language resolves to: the value itself when it is a supported locale,
 * `DEFAULT_LOCALE` for `null`, `undefined` or anything the list does not carry.
 *
 * @param value - The stored language, typically `users.language`.
 * @returns A locale every text builder can rely on.
 */
export function toLocale(value: string | null | undefined): Locale {
	return isLocale(value) ? value : DEFAULT_LOCALE;
}

/**
 * `localized` bound to one language, for a text builder that resolves several keys for the same
 * recipient. Binding the language once is what keeps a builder from forgetting the third
 * argument on one of its keys and sending a recipient a sentence in two languages.
 *
 * @param language - The recipient's locale.
 * @returns A function with `localized`'s first two parameters.
 */
export function localizer(language: Locale) {
	return (translationKey: TranslationKey, replace?: Record<string, string>) =>
		localized(translationKey, replace, language);
}

/**
 * Translate a key outside React, where no i18next instance is at hand — a server-side push text,
 * a mail subject, a table header built at module scope.
 *
 * A key the table does not carry, or one that points at a branch rather than a leaf, comes back
 * as the key itself, so a gap shows up as a visible key rather than as an empty string.
 *
 * @param _translationkey - The dotted path of the leaf, as `en.locale.ts` spells it.
 * @param replace - Values for the `{placeholder}`s the leaf carries.
 * @param language - The table to read; English unless asked otherwise.
 * @returns The translated text with its placeholders filled, or the key when nothing is found.
 */
export function localized(
	_translationkey: TranslationKey,
	replace?: Record<string, string>,
	language: Locale = "en",
): string {
	const translated = resolveLeaf(TABLES[language], _translationkey);
	if (translated === null) return _translationkey;
	if (!replace) return translated;
	return Object.entries(replace).reduce(
		(acc, [key, value]) => acc.replace(`{${key}}`, value),
		translated,
	);
}

/**
 * Walk a locale table one segment at a time to the string leaf a key names, or `null` when the
 * path is missing or ends on a branch. A missing path is logged, a branch is not.
 */
function resolveLeaf(table: unknown, translationKey: string): string | null {
	const keys = ["translation"].concat(translationKey.split("."));
	let currentMap: unknown = table;
	for (const key of keys) {
		const resolved = resolveSegment(currentMap, key);
		if (resolved === null) {
			console.info(`[localized] Translation missing for key: ${translationKey}`);
			return null;
		}
		currentMap = resolved;
	}
	return typeof currentMap === "string" ? currentMap : null;
}

/**
 * One level of the locale tree, or `null` when the segment names nothing there.
 *
 * The literal segment wins over its snake_cased form, and the order is load-bearing: some leaves
 * are database enum values and carry the hyphen the database uses — `in-progress`, `in-transit`,
 * `ember-blaze`. Normalising those first names a key no locale carries, so the caller would be
 * handed the raw key path where the label belongs. Snake_casing stays as the fallback for a caller
 * whose path is spelled differently from the locale's — a camelCase subject, a hyphenated type.
 */
function resolveSegment(currentMap: unknown, segment: string): unknown {
	if (!currentMap || typeof currentMap !== "object") return null;
	const map = currentMap as Record<string, unknown>;
	if (segment in map) return map[segment];
	const normalized = snakeCase(segment);
	if (normalized in map) return map[normalized];
	return null;
}
